import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { messaging, getToken, onMessage } from "../config/firebase";
import { getOrCreateClientDeviceId } from "../utils/deviceId";

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || "";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  "https://qylmox.in/api";
const FCM_TOKEN_CACHE_KEY = "fcm_registration_token";
const FCM_RETRY_AT_KEY = "fcm_registration_retry_at";

async function registerFcmServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/firebase-messaging-sw.js", { scope: "/" });
  } catch (error) {
    console.error("[FCM] Failed to register firebase messaging service worker:", error?.message || error);
    return null;
  }
}

async function persistFcmToken({ jwtToken, fcmToken }) {
  const deviceId = getOrCreateClientDeviceId();
  const payload = {
    deviceId,
    token: fcmToken,
    platform: "web",
    deviceInfo: {
      model: navigator.userAgent.slice(0, 100),
      os: navigator.platform || "web",
    },
  };

  const endpoints = [`${API_BASE}/notifications/fcm-token`, `${API_BASE}/notificationswebapp/fcm-token`];
  let lastError = null;

  for (const endpoint of endpoints) {
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
          "x-device-id": deviceId,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        return true;
      }

      lastError = new Error(`HTTP ${res.status} @ ${endpoint}`);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Failed to persist FCM token");
}

function NotificationSetup() {
  const user = useSelector((state) => state.auth?.user);
  const token = useSelector((state) => state.auth?.token);
  const registeredRef = useRef(false);
  const recentForegroundNotificationsRef = useRef(new Map());
  const retryTimerRef = useRef(null);

  useEffect(() => {
    registeredRef.current = false;
    recentForegroundNotificationsRef.current.clear();
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, [user?._id, user?.id, token]);

  useEffect(() => {
    if (!user?._id && !user?.id) return;
    if (typeof window === "undefined") return;
    if (!("Notification" in window)) return;
    if (!messaging) return;
    if (!VAPID_KEY) {
      console.warn("[FCM] VAPID key not configured");
      return;
    }
    if (registeredRef.current) return;

    let unsubscribe = null;
    let cancelled = false;

    const scheduleRetry = (delayMs = 30000) => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      localStorage.setItem(FCM_RETRY_AT_KEY, String(Date.now() + delayMs));
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        if (!cancelled) {
          setup();
        }
      }, delayMs);
    };

    const setup = async () => {
      try {
        const jwtToken = token || localStorage.getItem("token") || localStorage.getItem("authToken");
        if (!jwtToken) return;
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
          scheduleRetry(60000);
          return;
        }

        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          console.log("[FCM] Notification permission denied");
          return;
        }

        const registration =
          (await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js")) ||
          (await registerFcmServiceWorker()) ||
          (await navigator.serviceWorker.ready.catch(() => null));

        const fcmToken = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration || undefined,
        });

        if (!fcmToken) {
          console.warn("[FCM] Failed to get token");
          scheduleRetry(45000);
          return;
        }

        console.log("[FCM] Token obtained, registering with backend...");
        await persistFcmToken({ jwtToken, fcmToken });
        registeredRef.current = true;
        localStorage.setItem(FCM_TOKEN_CACHE_KEY, fcmToken);
        localStorage.removeItem(FCM_RETRY_AT_KEY);
        console.log("[FCM] Token registered successfully");
      } catch (err) {
        const message = String(err?.message || err || "");
        if (message.includes("installations/app-offline")) {
          console.warn("[FCM] App is offline, deferring token setup.");
        } else {
          console.error("[FCM] Setup error:", err?.message || err);
        }
        scheduleRetry(60000);
      }
    };

    setup();

    const handleVisibilityRetry = () => {
      if (document.visibilityState !== "visible") return;
      if (registeredRef.current) return;
      const retryAt = Number(localStorage.getItem(FCM_RETRY_AT_KEY) || 0);
      if (!retryAt || retryAt <= Date.now()) {
        setup();
      }
    };

    window.addEventListener("online", handleVisibilityRetry);
    document.addEventListener("visibilitychange", handleVisibilityRetry);

    try {
      unsubscribe = onMessage(messaging, (payload) => {
        const notificationId =
          payload?.data?.notificationId ||
          payload?.data?.taskNotificationId ||
          payload?.messageId ||
          payload?.data?.broadcastId ||
          `${payload?.notification?.title || payload?.data?.title || "notification"}-${payload?.notification?.body || payload?.data?.body || ""}`;
        const title =
          payload?.notification?.title ||
          payload?.data?.title ||
          "Qylmox Notification";
        const body =
          payload?.notification?.body ||
          payload?.data?.body ||
          "You have a new notification";

        const now = Date.now();
        const lastShownAt = recentForegroundNotificationsRef.current.get(notificationId) || 0;
        if (now - lastShownAt < 10000) {
          return;
        }
        recentForegroundNotificationsRef.current.set(notificationId, now);

        recentForegroundNotificationsRef.current.forEach((seenAt, key) => {
          if (now - seenAt > 60000) {
            recentForegroundNotificationsRef.current.delete(key);
          }
        });

        if (document.visibilityState === "visible" && document.hasFocus()) {
          return;
        }

        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(title, {
            body,
            icon: "/logo192.png",
          });
        }
      });
    } catch (err) {
      console.error("[FCM] onMessage listener error:", err?.message || err);
    }

    return () => {
      cancelled = true;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      window.removeEventListener("online", handleVisibilityRetry);
      document.removeEventListener("visibilitychange", handleVisibilityRetry);
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [user?._id, user?.id, token]);

  return null;
}

export default NotificationSetup;
